import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Group,
  Button,
  Table,
  TextInput,
  Modal,
  Textarea,
  Text,
  ActionIcon,
  Badge,
  ThemeIcon,
  Loader,
  Center,
  Alert,
  Card,
} from "@mantine/core";
import {
  IconPlus,
  IconTrash,
  IconTags,
  IconUpload,
  IconAlertCircle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { api } from "@/lib/api";

export default function ClassnameList() {
  const [names, setNames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Single add
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  // Bulk add
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.name.list();
      setNames(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.name.create({ name: newName.trim() });
      notifications.show({
        title: "Амжилттай",
        message: `"${newName}" нэмэгдлээ`,
        color: "green",
      });
      setNewName("");
      setAddOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const handleBulk = async () => {
    const items = bulkText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (items.length === 0) return;

    setBulkSaving(true);
    try {
      const res = await api.name.bulk(items);
      notifications.show({
        title: "Амжилттай",
        message: `${res.added} шинэ нэр нэмэгдлээ`,
        color: "green",
      });
      setBulkText("");
      setBulkOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally {
      setBulkSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await api.name.remove(id);
      notifications.show({
        title: "Амжилттай",
        message: `"${name}" устгагдлаа`,
        color: "green",
      });
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>Таних нэрс</Title>
          <Badge variant="light" size="lg">
            {names.length}
          </Badge>
        </Group>
        <Group gap="xs">
          <Button
            variant="light"
            leftSection={<IconUpload size={16} />}
            onClick={() => setBulkOpen(true)}
            size="sm"
          >
            Бөөнөөр нэмэх
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setAddOpen(true)}
            size="sm"
          >
            Нэр нэмэх
          </Button>
        </Group>
      </Group>

      <Alert variant="light" color="blue" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">
          Таних нэрс нь тухайн төсөлд хамаарна. YOLO model-д зориулж bounding box
          тэмдэглэх болон датасет экспорт хийхэд ашиглагдана.
        </Text>
      </Alert>

      {loading ? (
        <Center h={300}>
          <Loader />
        </Center>
      ) : names.length === 0 ? (
        <Center h={300}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} variant="light" color="gray" radius="xl">
              <IconTags size={24} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              Таних нэр бүртгээгүй байна
            </Text>
            <Button variant="light" size="xs" onClick={() => setBulkOpen(true)}>
              Бөөнөөр нэмэх
            </Button>
          </Stack>
        </Center>
      ) : (
        <Card shadow="xs" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={400}>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Түлхүүр</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={60}>Устгах</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {names.map((n, idx) => (
                  <Table.Tr key={n._id}>
                    <Table.Td c="dimmed" fz="sm">
                      {idx + 1}
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="md">
                        {n.name}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {n.key || "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        radius="md"
                        color="red"
                        onClick={() => handleDelete(n._id, n.name)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      {/* Single Add Modal */}
      <Modal
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        title={<Text fw={600}>Таних нэр нэмэх</Text>}
        size="sm"
        centered
      >
        <Stack>
          <TextInput
            label="Нэр"
            placeholder="Жишээ: PLATE, А, 0"
            value={newName}
            onChange={(e) => setNewName(e.currentTarget.value)}
            size="sm"
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          />
          <Button onClick={handleAdd} loading={saving} size="sm">
            Нэмэх
          </Button>
        </Stack>
      </Modal>

      {/* Bulk Add Modal */}
      <Modal
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title={<Text fw={600}>Бөөнөөр нэмэх</Text>}
        size="md"
        centered
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Нэрсийг мөр бүрд нэг эсвэл таслалаар тусгаарлаж бичнэ үү.
          </Text>
          <Textarea
            label="Таних нэрс"
            placeholder={"PLATE\nА\nБ\nВ\n0\n1\n2\n3"}
            value={bulkText}
            onChange={(e) => setBulkText(e.currentTarget.value)}
            minRows={8}
            autosize
            size="sm"
          />
          <Text size="xs" c="dimmed">
            {bulkText
              .split(/[\n,]+/)
              .map((s) => s.trim())
              .filter(Boolean).length}{" "}
            нэр оруулсан
          </Text>
          <Button onClick={handleBulk} loading={bulkSaving} size="sm">
            Нэмэх
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
